import { SearchContainer } from './style';
import { Box, Button, FormControlLabel, Switch } from '@mui/material';
import { SearchInput } from 'components/SearchInput';
import { ChangeEvent } from 'react';
import { makeStringFromArrays } from 'utils';
import { useDispatch } from 'react-redux';
import { fetchSandboxMembersCsv } from 'SandboxModule/thunks';
import { useTeamMemberTableBulkSelectMenu } from 'TeamModule/hooks/useTeamMemberTableBulkSelectMenu';
import { useNotification } from 'hooks/useNotification';
import { useFeatureFlags } from 'hooks/useFeatureFlags';

export const TeamMembersTableHeader: React.FC<{
  teamId: number;
  selectedAccountIds: number[];
  hideArchivedMembers: boolean;
  onToggleHideArchivedMembers: () => void;
  searchMember: (e: ChangeEvent<HTMLInputElement>) => void;
  searchedMemberName: string;
  clearAll: () => void;
  /** Tenant name for API context - ensures actions target the correct tenant */
  tenant?: string;
  /** Realm ID for API context - ensures actions target the correct realm */
  realmId?: string;
}> = ({
  teamId,
  selectedAccountIds,
  clearAll,
  searchedMemberName,
  searchMember,
  hideArchivedMembers,
  onToggleHideArchivedMembers,
  tenant,
  realmId,
}) => {
  const { isExportMemberCsvEnabled } = useFeatureFlags();
  const dispatch = useDispatch();
  const { showError } = useNotification();

  const { openTeamMemberTableBulkSelectMenu, BulkSelectMenuComponent } =
    useTeamMemberTableBulkSelectMenu({
      teamId,
      accountIds: selectedAccountIds,
      onConfirm: clearAll,
      tenant,
      realmId,
    });

  const handleDownloadSandboxMembersCsv = () => {
    if (teamId) {
      // TODO: Fix with Redux Toolkit configureStore migration
      dispatch(
        fetchSandboxMembersCsv({
          teamId,
          onSuccess: ({ response }) => {
            const url = response.data.download_url;
            window.open(url, '_blank');
          },
          onFailure: ({ error }: any) => {
            const { data } = error || {};
            const { errors = [], message } = data || {};
            if (errors.length) {
              showError(makeStringFromArrays(errors, true));
            } else {
              const apiMessage = message || error?.message;
              const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '';
              showError(`Failed to download CSV${apiMessageSuffix}`);
            }
          },
        }) as any,
      );
    }
  };

  return (
    <SearchContainer>
      <Box sx={{ minWidth: 300, maxWidth: 400 }}>
        <SearchInput
          placeholder="Search by account ID, name, or email..."
          value={searchedMemberName}
          onChange={searchMember}
          size="small"
          fullWidth
        />
      </Box>
      <Box display={'flex'}>
        <FormControlLabel
          control={
            <Switch
              checked={hideArchivedMembers}
              onChange={onToggleHideArchivedMembers}
              name="archivedMembers"
              color="primary"
            />
          }
          label="Hide Archived Members"
        />
        {isExportMemberCsvEnabled ? (
          <Box marginRight={4}>
            <Button variant="outlined" color="primary" onClick={handleDownloadSandboxMembersCsv}>
              Download CSV
            </Button>
          </Box>
        ) : (
          <></>
        )}
        <Button
          variant="outlined"
          color="primary"
          disabled={selectedAccountIds.length === 0}
          onClick={(e) => {
            openTeamMemberTableBulkSelectMenu({ target: e.currentTarget });
          }}
        >
          Modify {selectedAccountIds.length || ''}
        </Button>
        {BulkSelectMenuComponent}
      </Box>
    </SearchContainer>
  );
};
